import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { paymentService } from '../services/paymentService';
import toast from 'react-hot-toast';
import { useSEO } from '../hooks/useSEO';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : Promise.resolve(null);

const PaymentForm = ({ orderData, setOrderData, cart, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(cart.total);

  const calculateShipping = useCallback(async () => {
    setShippingLoading(true);
    setPaymentError(null);
    try {
      const result = await paymentService.calculateShipping(orderData.shippingInfo.country, cart.items);
      if (result && typeof result.shippingCost === 'number') {
        setShippingCost(result.shippingCost);
        setPaymentError(null);
      } else {
        setShippingCost(9.99);
      }
    } catch (error) {
      setShippingCost(9.99);
    } finally {
      setShippingLoading(false);
    }
  }, [orderData.shippingInfo.country, cart.items]);

  useEffect(() => {
    if (orderData.shippingInfo.country && cart.items.length > 0) {
      calculateShipping();
    }
  }, [orderData.shippingInfo.country, cart.items, calculateShipping]);

  useEffect(() => {
    setTotalAmount(cart.total + shippingCost);
  }, [cart.total, shippingCost]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({
      ...prev,
      shippingInfo: { ...prev.shippingInfo, [name]: value }
    }));
    if (paymentError) setPaymentError(null);
  };

  const validateForm = () => {
    const { shippingInfo } = orderData;
    const requiredFields = ['firstName', 'lastName', 'email', 'address', 'city', 'postalCode', 'country'];
    for (const field of requiredFields) {
      if (!shippingInfo[field] || shippingInfo[field].trim() === '') {
        setPaymentError(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shippingInfo.email)) {
      setPaymentError('Please enter a valid email address');
      return false;
    }
    if (!/^[a-zA-Z0-9\s-]{3,10}$/.test(shippingInfo.postalCode)) {
      setPaymentError('Please enter a valid postal code');
      return false;
    }
    return true;
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setPaymentError('Payment system not loaded. Please refresh and try again.');
      return;
    }
    if (!validateForm()) return;
    if (cart.items.length === 0) {
      setPaymentError('Your cart is empty');
      return;
    }
    if (!user) {
      setPaymentError('Please sign in to complete your purchase');
      return;
    }
    setProcessing(true);
    setPaymentError(null);
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');
      const completeOrderData = { ...orderData, items: cart.items };
      const { clientSecret, error: intentError, orderNumber } = await paymentService.createPaymentIntent({
        amount: cart.total,
        currency: 'GBP',
        orderData: completeOrderData,
        shippingCost: shippingCost
      });
      if (intentError) throw new Error(intentError);
      if (!clientSecret) throw new Error('Failed to create payment intent');
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${orderData.shippingInfo.firstName} ${orderData.shippingInfo.lastName}`,
            email: orderData.shippingInfo.email,
            phone: orderData.shippingInfo.phone || undefined,
            address: {
              line1: orderData.shippingInfo.address,
              city: orderData.shippingInfo.city,
              postal_code: orderData.shippingInfo.postalCode,
              country: orderData.shippingInfo.country,
            },
          },
        },
      });
      if (stripeError) throw new Error(stripeError.message);
      if (paymentIntent.status === 'succeeded') {
        toast.success('Payment successful!');
        onPaymentSuccess(paymentIntent, { ...completeOrderData, shippingCost, orderNumber });
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (error) {
      setPaymentError(error.message || 'Payment failed. Please try again.');
      toast.error(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': { color: '#aab7c4' },
        fontFamily: 'Inter, system-ui, sans-serif',
        lineHeight: '24px',
      },
      invalid: { color: '#fa755a', iconColor: '#fa755a' }
    },
    hidePostalCode: true,
  };

  const countries = paymentService.getShippingCountries();

  return (
    <form onSubmit={handlePayment} className="space-y-6">
      {user && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon="check-circle" className="text-green-500 text-lg" />
            <div>
              <p className="text-body-sm text-green-800 font-medium">Payment System Ready</p>
              <p className="text-body-sm text-green-600">Secure payment processing is active.</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-h5 text-dark mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon="truck" className="text-blue-500" />
          Shipping Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-label text-gray-700 mb-1">First Name *</label>
            <input type="text" name="firstName" required value={orderData.shippingInfo.firstName} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body" placeholder="John" />
          </div>
          <div>
            <label className="block text-label text-gray-700 mb-1">Last Name *</label>
            <input type="text" name="lastName" required value={orderData.shippingInfo.lastName} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body" placeholder="Doe" />
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-label text-gray-700 mb-1">Email Address *</label>
            <input type="email" name="email" required value={orderData.shippingInfo.email} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body" placeholder="john@example.com" />
          </div>
          <div>
            <label className="block text-label text-gray-700 mb-1">Phone Number</label>
            <input type="tel" name="phone" value={orderData.shippingInfo.phone} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body" placeholder="+44 123 456 7890" />
          </div>
          <div>
            <label className="block text-label text-gray-700 mb-1">Street Address *</label>
            <input type="text" name="address" required value={orderData.shippingInfo.address} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body" placeholder="123 Main Street" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-label text-gray-700 mb-1">City *</label>
              <input type="text" name="city" required value={orderData.shippingInfo.city} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body" placeholder="London" />
            </div>
            <div>
              <label className="block text-label text-gray-700 mb-1">Postal Code *</label>
              <input type="text" name="postalCode" required value={orderData.shippingInfo.postalCode} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body" placeholder="SW1A 1AA" />
            </div>
          </div>
          <div>
            <label className="block text-label text-gray-700 mb-1">Country *</label>
            <select name="country" required value={orderData.shippingInfo.country} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 border border-transparent focus:border-blue-500 text-body">
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>{country.flag} {country.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {orderData.shippingInfo.country && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-body font-medium text-blue-800 flex items-center gap-2">
              <FontAwesomeIcon icon="shipping-fast" className="text-blue-600" />
              Shipping Cost:
            </span>
            {shippingLoading ? (
              <FontAwesomeIcon icon="spinner" className="text-blue-600 animate-spin" />
            ) : (
              <span className="text-h5 text-blue-800">
                {shippingCost === 0 ? <span className="text-green-600">FREE ✨</span> : `£${shippingCost.toFixed(2)}`}
              </span>
            )}
          </div>
          {shippingCost === 0 && cart.total >= 50 && (
            <p className="text-body-sm text-green-600 mt-2 flex items-center gap-1">
              <FontAwesomeIcon icon="check-circle" />
              🎉 You qualify for free shipping on orders over £50!
            </p>
          )}
        </div>
      )}

      {stripePublishableKey && (
        <div>
          <h3 className="text-h5 text-dark mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon="credit-card" className="text-green-500" />
            Payment Information
          </h3>
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 border border-gray-200">
            <label className="block text-label text-gray-700 mb-3">Card Details *</label>
            <div className="bg-white rounded-xl p-4 border-2 border-gray-200 focus-within:border-blue-500 transition-all duration-300">
              <CardElement options={cardElementOptions} />
            </div>
            <div className="mt-3 flex items-center gap-4 text-caption text-gray-600">
              <div className="flex items-center gap-1"><FontAwesomeIcon icon="credit-card" className="text-blue-500" /><span>Visa</span></div>
              <div className="flex items-center gap-1"><FontAwesomeIcon icon="credit-card" className="text-red-500" /><span>Mastercard</span></div>
              <div className="flex items-center gap-1"><FontAwesomeIcon icon="credit-card" className="text-blue-600" /><span>Amex</span></div>
              <span className="text-gray-500">Card payments only</span>
            </div>
          </div>
        </div>
      )}

      {paymentError && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon="exclamation-triangle" className="text-red-500 text-lg" />
            <div>
              <p className="text-body-sm text-red-800 font-medium">Payment Error</p>
              <p className="text-body-sm text-red-600">{paymentError}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-4 border border-green-200">
        <h4 className="text-h5 text-gray-800 mb-3">Order Total</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-body-sm">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-medium">£{cart.total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-body-sm">
            <span className="text-gray-600">Shipping:</span>
            <span className="font-medium">
              {shippingLoading ? <FontAwesomeIcon icon="spinner" className="animate-spin text-blue-500" /> : shippingCost === 0 ? <span className="text-green-600">FREE</span> : `£${shippingCost.toFixed(2)}`}
            </span>
          </div>
          <div className="border-t border-gray-300 pt-2 flex justify-between text-h5">
            <span>Total:</span>
            <span className="text-gradient-blue-pink">£{totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {stripePublishableKey ? (
        <button type="submit" disabled={processing || !stripe || !elements || shippingLoading || !user} className="w-full btn btn-primary text-btn-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed">
          {processing ? (
            <><FontAwesomeIcon icon="spinner" className="animate-spin" /><span>Processing Payment...</span></>
          ) : !user ? (
            <><FontAwesomeIcon icon="user-times" /><span>Sign In Required</span></>
          ) : !stripe || !elements ? (
            <><FontAwesomeIcon icon="spinner" className="animate-spin" /><span>Loading Payment System...</span></>
          ) : (
            <><FontAwesomeIcon icon="lock" /><span>Pay Securely £{totalAmount.toFixed(2)}</span></>
          )}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-body text-gray-600 mb-4">Payment processing is currently unavailable</p>
          <Link to="/contact" className="btn btn-primary text-btn-lg py-4">
            <FontAwesomeIcon icon="envelope" />
            <span>Contact Support to Complete Order</span>
          </Link>
        </div>
      )}

      {stripePublishableKey && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-body-sm text-gray-500">
          <div className="flex items-center gap-2"><FontAwesomeIcon icon="shield-alt" className="text-green-500" /><span>256-bit SSL Encryption</span></div>
          <div className="flex items-center gap-2"><FontAwesomeIcon icon="check-circle" className="text-blue-500" /><span>Powered by Stripe</span></div>
          <div className="flex items-center gap-2"><FontAwesomeIcon icon="credit-card" className="text-purple-500" /><span>Card Payments Only</span></div>
        </div>
      )}
    </form>
  );
};

const PaymentPage = () => {
  useSEO({
    title: "Secure Payment | AM CARDMATE",
    description: "Complete your purchase securely with our encrypted payment system. Multiple payment options available.",
    keywords: "payment, checkout, secure payment, credit card, purchase"
  });

  const { cart, loading: cartLoading } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [orderData, setOrderData] = useState({
    shippingInfo: {
      firstName: '',
      lastName: '',
      email: user?.email || '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'GB'
    }
  });

  useEffect(() => {
    if (location.state?.orderData) {
      setOrderData(prev => ({
        ...prev,
        ...location.state.orderData,
        shippingInfo: { ...prev.shippingInfo, ...location.state.orderData.shippingInfo }
      }));
    }
  }, [location.state]);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/payment' } } });
    } else if (cart.items.length === 0) {
      navigate('/cart');
    }
  }, [user, cart.items, navigate]);

  const handlePaymentSuccess = (paymentIntent, finalOrderData) => {
    navigate('/payment-confirmation', { state: { paymentIntent, orderData: finalOrderData, cart } });
  };

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon="spinner" className="text-4xl text-blue-500 animate-spin mb-4" />
          <p className="text-body text-gray-600">Loading payment page...</p>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 sm:py-12 px-4 sm:px-6">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-h2 text-dark mb-4">
              Complete Your <span className="text-gradient-blue-pink">Payment</span>
            </h1>
            <p className="text-body-lg text-gray-600">Secure card payment powered by Stripe</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6 sticky top-6">
                <h2 className="text-h4 text-dark mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="receipt" className="text-purple-500" />
                  Order Summary
                </h2>
                
                <div className="space-y-4 mb-6">
                  {cart.items.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="flex items-center gap-4 p-3 bg-white/50 rounded-2xl">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-contain rounded-xl" />
                        ) : (
                          <FontAwesomeIcon icon="credit-card" className="text-2xl text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-body-sm font-semibold text-dark truncate">{item.name}</h3>
                        <p className="text-caption text-gray-600">Qty: {item.quantity}</p>
                        {item.description && <p className="text-caption text-gray-500 truncate">{item.description}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-body-sm font-bold">£{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex justify-between text-body-sm">
                    <span className="text-gray-600">Subtotal ({cart.items.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                    <span className="font-medium">£{cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-body-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium text-blue-600">Calculated below</span>
                  </div>
                  <div className="flex justify-between text-h5 border-t border-gray-200 pt-3">
                    <span>Total</span>
                    <span className="text-gradient-blue-pink">£{cart.total.toFixed(2)}+</span>
                  </div>
                  <p className="text-caption text-gray-500 text-center">Final total will be shown before payment</p>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 gap-3 text-caption text-gray-600">
                    <div className="flex items-center gap-2"><FontAwesomeIcon icon="shield-alt" className="text-green-500" /><span>SSL Secure Checkout</span></div>
                    <div className="flex items-center gap-2"><FontAwesomeIcon icon="truck" className="text-blue-500" /><span>Free shipping on £50+ to Europe</span></div>
                    <div className="flex items-center gap-2"><FontAwesomeIcon icon="headset" className="text-purple-500" /><span>24/7 customer support</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 order-1 lg:order-2">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8">
                <h2 className="text-h4 text-dark mb-6">Payment Details</h2>
                <PaymentForm orderData={orderData} setOrderData={setOrderData} cart={cart} onPaymentSuccess={handlePaymentSuccess} />
                <div className="mt-8 text-center">
                  <Link to="/cart" className="text-body-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-2 hover:gap-3 transition-all duration-300">
                    <FontAwesomeIcon icon="arrow-left" />
                    Back to cart
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Elements>
  );
};

export default PaymentPage;